import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useCart } from "../context/cartcontext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../data/products";
import { deliveryLocations } from "./deliveryLocations";
import { initializeCheckoutPayment, validateCheckoutPromoCode } from "../services/orders";
import { getActiveProductsByIds } from "../services/storefrontProducts";
import { getLgaOptions } from "../utils/deliveryPricing";
import {
  readCheckoutDraft,
  saveCheckoutDraft,
  savePendingCheckoutReference,
} from "../utils/checkoutStorage";
import { collectCartInventoryIssues } from "../utils/inventory";

const Checkout = () => {
  const { cart, cartSubtotal, loading: cartLoading } = useCart();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const savedDraft = readCheckoutDraft();
  const draftCheckoutState = location.state ?? savedDraft?.checkoutState ?? {};

  const [customerDetails, setCustomerDetails] = useState({
    fullName: savedDraft?.customerDetails?.fullName || "",
    email: savedDraft?.customerDetails?.email || user?.email || "",
    phone: savedDraft?.customerDetails?.phone || "",
    address: savedDraft?.customerDetails?.address || "",
    note: savedDraft?.customerDetails?.note || "",
  });
  const [selectedState, setSelectedState] = useState(draftCheckoutState.selectedState || "");
  const [selectedLGA, setSelectedLGA] = useState(draftCheckoutState.selectedLGA || "");
  const [errorMessage, setErrorMessage] = useState(location.state?.message || "");
  const [paymentStage, setPaymentStage] = useState("idle");
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromotion, setAppliedPromotion] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const availableStates = useMemo(() => Object.keys(deliveryLocations), []);
  const availableLGAs = useMemo(
    () => getLgaOptions(deliveryLocations[selectedState]),
    [selectedState]
  );
  const orderTotal = useMemo(() => (cart.length > 0 ? Number(appliedPromotion?.total_amount ?? cartSubtotal) : 0), [appliedPromotion, cart.length, cartSubtotal]);
  const isRedirecting = paymentStage === "redirecting";

  useEffect(() => {
    if (selectedLGA && !availableLGAs.some((lga) => lga.name === selectedLGA)) {
      setSelectedLGA("");
    }
  }, [availableLGAs, selectedLGA]);

  useEffect(() => {
    if (user?.email && !customerDetails.email) {
      setCustomerDetails((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [customerDetails.email, user?.email]);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async () => {
      const productIds = [...new Set(cart.map((item) => item.productId).filter(Boolean))];

      if (!productIds.length) {
        if (!cancelled) {
          setInventoryProducts([]);
          setInventoryLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setInventoryLoading(true);
      }

      try {
        const products = await getActiveProductsByIds(productIds);
        if (!cancelled) {
          setInventoryProducts(products);
        }
      } catch (_error) {
        if (!cancelled) {
          setInventoryProducts([]);
        }
      } finally {
        if (!cancelled) {
          setInventoryLoading(false);
        }
      }
    };

    loadInventory();

    return () => {
      cancelled = true;
    };
  }, [cart]);

  const inventoryIssues = useMemo(
    () => collectCartInventoryIssues(cart, inventoryProducts),
    [cart, inventoryProducts]
  );
  const checkoutBlocked = inventoryLoading || inventoryIssues.length > 0;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCustomerDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateCheckout = () => {
    if (
      !customerDetails.fullName.trim() ||
      !customerDetails.email.trim() ||
      !customerDetails.phone.trim() ||
      !customerDetails.address.trim()
    ) {
      return "Enter your name, email, phone number, and delivery address.";
    }

    if (!selectedState || !selectedLGA) {
      return "Select a delivery state and LGA before payment.";
    }

    if (inventoryIssues.length > 0) {
      return inventoryIssues[0].message;
    }

    if (!cart.length || orderTotal <= 0) {
      return "Your cart is empty.";
    }

    return "";
  };

  const handleContinueToPayment = async () => {
    const validationError = validateCheckout();

    if (validationError) {
      setErrorMessage(validationError);
      return;
      }

      setPaymentStage("redirecting");
      setErrorMessage("");

    const draft = {
      checkoutState: {
        selectedState,
        selectedLGA,
        deliveryProvider: "manual",
      },
      customerDetails,
      cartSnapshot: cart,
    };

    saveCheckoutDraft(draft);

    try {
      const payment = await initializeCheckoutPayment({
        ...customerDetails,
        selectedState,
        selectedLGA,
        promoCode: appliedPromotion?.promo_code || "",
      });

      savePendingCheckoutReference(payment.reference);
      window.location.href = payment.authorization_url;
    } catch (error) {
      console.error("Unable to initialize checkout:", {
        error,
        customerEmail: customerDetails.email,
        selectedState,
        selectedLGA,
      });
      setPaymentStage("idle");
      setErrorMessage(error.message || "Unable to redirect to payment.");
    }
  };

  const handleApplyPromoCode = async () => {
    const normalizedCode = promoCode.trim();
    if (!normalizedCode) {
      setAppliedPromotion(null);
      setErrorMessage("Enter a promo code.");
      return;
    }
    setPromoLoading(true);
    setErrorMessage("");
    try {
      const promotion = await validateCheckoutPromoCode(normalizedCode);
      setAppliedPromotion({
        ...promotion,
        total_amount: promotion.totalAmount,
        promo_code: promotion.promoCode,
      });
    } catch (error) {
      setAppliedPromotion(null);
      setErrorMessage(error.message || "Unable to validate promo code.");
    } finally {
      setPromoLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen px-6 py-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] bg-white p-10 text-center shadow-lg shadow-black/5">
          <p className="text-lg text-slate-700">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (user && cartLoading) {
    return (
      <div className="min-h-screen px-6 py-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] bg-white p-10 text-center shadow-lg shadow-black/5">
          <p className="text-lg text-slate-700">Preparing your cart...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: { pathname: "/checkout" } }} />;
  }

  if (cart.length === 0) {
    return <Navigate to="/cart" replace />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,249,195,0.9),_transparent_35%),linear-gradient(180deg,#fffdf7_0%,#ffffff_100%)] px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-[0.28em] text-amber-700">
              Checkout details
            </p>
            <h1 className="mt-3 text-4xl font-bold text-slate-900">
              Finalize your order
            </h1>
            <p className="mt-3 max-w-2xl text-lg leading-8 text-slate-600">
              Confirm your contact and delivery details, then continue to the Paystack hosted
              checkout page.
            </p>
          </div>
          <Link
            to="/cart"
            className="inline-flex rounded-full border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-900 transition hover:border-slate-400"
          >
            Back to cart
          </Link>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-[2rem] bg-white p-6 shadow-lg shadow-black/5">
            <h2 className="text-2xl font-semibold text-slate-900">Customer information</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Full name</span>
                <input
                  type="text"
                  name="fullName"
                  value={customerDetails.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Email</span>
                <input
                  type="email"
                  name="email"
                  value={customerDetails.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Phone number</span>
                <input
                  type="tel"
                  name="phone"
                  value={customerDetails.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Delivery state</span>
                <select
                  value={selectedState}
                  onChange={(event) => setSelectedState(event.target.value)}
                  disabled={isRedirecting}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400 disabled:bg-slate-100"
                >
                  <option value="">Select a state</option>
                  {availableStates.map((stateName) => (
                    <option key={stateName} value={stateName}>
                      {stateName}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Delivery LGA</span>
                <select
                  value={selectedLGA}
                  onChange={(event) => setSelectedLGA(event.target.value)}
                  disabled={!selectedState || isRedirecting}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400 disabled:bg-slate-100"
                >
                  <option value="">Select an LGA</option>
                  {availableLGAs.map((lga) => (
                    <option key={lga.name} value={lga.name}>
                      {lga.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3">
                <p className="text-sm font-medium text-amber-800">Delivery</p>
                <p className="mt-2 font-semibold text-slate-900">Arranged separately</p>
                <p className="mt-1 text-sm text-slate-600">Delivery fee: Paid directly to rider/dispatch service</p>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Delivery address</span>
              <textarea
                name="address"
                value={customerDetails.address}
                onChange={handleChange}
                rows="4"
                placeholder="Enter the exact delivery address"
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400"
              />
            </label>

            <div className="mt-5 rounded-2xl border border-lime-200 bg-lime-50 px-4 py-3 text-sm text-slate-700">
              Delivery is arranged separately. A Zea Mays representative will contact you after your order is confirmed to arrange a suitable local rider or dispatch service. Delivery fees are paid directly to the rider/dispatch service and are not included in your order total.
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Order note</span>
              <textarea
                name="note"
                value={customerDetails.note}
                onChange={handleChange}
                rows="3"
                placeholder="Any extra instructions for the order"
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-amber-400"
              />
            </label>

            {inventoryIssues.length > 0 ? (
              <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold">Payment is blocked until your cart is updated.</p>
                <ul className="mt-2 space-y-1">
                  {inventoryIssues.map((issue) => (
                    <li key={`${issue.productId}-${issue.productName}`}>{issue.message}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {inventoryLoading ? (
              <p className="mt-4 text-sm text-amber-700">Checking live stock...</p>
            ) : null}

            <button
              type="button"
              onClick={handleContinueToPayment}
              disabled={isRedirecting || checkoutBlocked}
              className="mt-8 w-full rounded-2xl bg-amber-400 px-6 py-4 font-semibold text-slate-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-amber-200"
            >
              {isRedirecting ? "Redirecting to payment..." : "Pay now with Paystack"}
            </button>
            {errorMessage ? (
              <p className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errorMessage}
              </p>
            ) : null}
          </section>

          <aside className="h-fit rounded-[2rem] bg-slate-900 p-6 text-white shadow-xl shadow-black/10">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Order summary</p>

            <div className="mt-6 space-y-4">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-4 rounded-2xl bg-slate-800/70 px-4 py-3"
                >
                  <div>
                    <p className="font-semibold text-white">{item.name}</p>
                    <p className="text-sm text-slate-400">
                      {item.selectedSize || item.optionLabel || "Standard"} x {item.quantity} · {formatPrice(item.unitPrice ?? item.price)} each
                    </p>
                  </div>
                  <p className="font-semibold text-amber-300">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-800/60 p-4">
              <p className="text-sm font-semibold text-white">Have a promo code?</p>
              <div className="mt-3 flex gap-2">
                <input
                  value={promoCode}
                  onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="min-w-0 flex-1 rounded-xl border-0 bg-white px-3 py-2 text-sm text-slate-900"
                  disabled={isRedirecting || promoLoading}
                />
                <button type="button" onClick={handleApplyPromoCode} disabled={isRedirecting || promoLoading} className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-bold text-slate-900 disabled:opacity-50">
                  {promoLoading ? "Checking" : "Apply"}
                </button>
              </div>
              {appliedPromotion ? <p className="mt-2 text-sm text-emerald-300">{appliedPromotion.promoCode} applied.</p> : null}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between text-slate-300">
                <span>Subtotal</span>
                <span>{formatPrice(cartSubtotal)}</span>
              </div>
              {appliedPromotion?.discountAmount > 0 ? (
                <div className="flex items-center justify-between text-emerald-300">
                  <span>Promotion discount</span>
                  <span>-{formatPrice(appliedPromotion.discountAmount)}</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between text-slate-300">
                <span>Delivery</span>
                <span>Arranged separately</span>
              </div>
              <div className="flex items-center justify-between text-slate-400">
                <span>Delivery fee</span>
                <span>Paid directly to rider/dispatch service</span>
              </div>
              <div className="h-px bg-slate-700" />
              <div className="flex items-center justify-between text-xl font-semibold">
                <span>Total</span>
                <span>{formatPrice(orderTotal)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
